import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ActorType,
  Currency,
  DeliveryType,
  PaymentType,
  ProductInfoType,
  ReceiptModel,
  SourceSystem,
  UiLabel
} from 'hx-services';
import { CommentModel } from 'hx-services';
import { FeedbackModel } from 'hx-services';

/**
 * `orders-archive.service.ts` — app-local HTTP client for `GET /api/vn/orders-archive/**`
 * (precedent: `feedbacks-new.service.ts`). Not part of `hx-services`/`vn-api` on purpose — this
 * resource is new and `tsgen` is not run in this task (see architecture §5.1/§6.1). Contract below
 * mirrors `tmp/orders-archive-architecture.md` §6.3 with the §8 amendments applied (opaque cursor,
 * no «доп. заказ», documents by eventId, payment channel, approximate-diff, Q8 asRecipient, ...).
 */

export const ARCHIVE_BASE = '/api/vn/orders-archive';

// ---------- shared shapes ----------

export type ArchiveStatus = 'RECEIVED' | 'CANCELLED' | 'REFUNDED';

export interface OrderRef {
  id: number;
  uniqueNumber: string;
}

export interface ActorRef {
  id?: number;
  name?: string;
  type?: ActorType;
  /** The staff directory's free text («оператор КЦ») — absent when nothing is written there. */
  position?: string;
  /**
   * 1d: the number a call-center operator occupies RIGHT NOW (the «добавочный», «вн. 117») — only in the answers that ask for it: the holder of an edit
   * lock / of a reservation in the state (N4: `current.lock.by`, `current.cart.owner`) and in `ordersCurrent.lockedByOther`. Absent for an operator who
   * has not picked a number, and in the list (`/list`, `/rows`) and the archive.
   */
  extension?: string;
}

export interface CurrencyAmount {
  currency: Currency;
  amount: number;
  orders: number;
}

export interface PaymentLine {
  type: PaymentType;
  value: number;
  discount?: number;
  cashback?: number;
  amount?: number;
}

export interface ArchiveFile {
  id: number;
  guid: string;
  fileName: string;
  mimeType?: string;
  size?: number;
  folder?: string;
  createdAt?: string;
  uploadedBy?: string;
}

export type PaymentChannel = 'LINK' | 'CASHBOX' | 'ON_RECEIPT' | 'INVOICE' | 'APP' | 'SITE';

// ---------- GET /scope ----------

export interface ArchiveScopeCountry {
  id: number;
  title: UiLabel;
  domain: string;
  currency?: Currency;
  phonePrefix: string;
  phoneLength: number;
  locale: string;
}

export interface ArchiveScopeCity {
  id: number;
  countryId: number;
  title: UiLabel;
}

export interface ArchiveScopeStore {
  id: number;
  cityId: number;
  countryId: number;
  brandId: number;
  title: UiLabel;
  address?: string;
  currency: Currency;
  timezone: string;
  /** Работает сейчас (не закрыт). */
  active: boolean;
  /** Магазин удалён — история всё равно видна оператору КЦ (§8.1). */
  removed: boolean;
}

export interface ArchiveScope {
  countries: ArchiveScopeCountry[];
  cities: ArchiveScopeCity[];
  stores: ArchiveScopeStore[];
}

// ---------- GET /list ----------

export type ArchiveDecorFilter = 'DECOR' | 'INSCRIPTION' | 'NONE';
export type ArchiveAttachmentsFilter = 'PHOTOS' | 'FILES' | 'NONE';
export type ArchiveClientTypeFilter = 'INDIVIDUAL' | 'LEGAL';
export type ArchiveFeedbackFilter = 'HAS' | 'LOW' | 'WAITING' | 'NONE';
export type ArchiveCommentsFilter = 'HAS' | 'NONE';
export type ArchiveSearchKind = 'PHONE' | 'PHONE_PARTIAL' | 'NUMBER' | 'NUMBER_CONTAINS' | 'NUMBER_OR_PHONE' | 'TEXT';

/** Filter/list state shared by /list and /summary — page keys are snake_case 1:1 with HTTP params. */
export interface ArchiveListParams {
  dateFrom?: string;
  dateTo?: string;
  countryIds?: number[];
  cityIds?: number[];
  storeIds?: number[];
  deliveryType?: DeliveryType;
  sources?: SourceSystem[];
  operatorId?: number;
  withClient?: boolean;
  decor?: ArchiveDecorFilter;
  attachments?: ArchiveAttachmentsFilter;
  clientType?: ArchiveClientTypeFilter;
  paymentTypes?: PaymentType[];
  feedback?: ArchiveFeedbackFilter;
  comments?: ArchiveCommentsFilter;
  productInfoIds?: number[];
  courierId?: number;
  fiscalNumber?: string;
  refundFrom?: string;
  refundTo?: string;
  status?: ArchiveStatus;
}

export interface ArchiveListPageParams extends ArchiveListParams {
  sort?: 'asc' | 'desc';
  limit?: number;
  /** Opaque cursor from `ArchiveOrderPage.next.cursor` (§8.3) — the frontend never parses it. */
  cursor?: string;
  q?: string;
  clientId?: number;
  /** Q8: phone search hit a client only as a *recipient* — keep the flat search view instead of switching to client mode. */
  clientSwitch?: boolean;
}

export interface ArchiveSearchContext {
  kind: ArchiveSearchKind;
  normalized: string;
  total?: number;
  totalCapped?: boolean;
  clientId?: number;
  refundOf?: OrderRef;
  inProgress?: { id: number; uniqueNumber: string; action: string };
  /** Address leg of a TEXT search was skipped (query too short or matched a store already) — §8.2. */
  addressSkipped?: boolean;
  /** Name search hit the 200-candidate cap — «уточните имя» (§8.2). */
  clientsCapped?: boolean;
}

export interface ArchiveOrderRow {
  id: number;
  uniqueNumber: string;
  date: string;
  receiveDate?: string;
  status: ArchiveStatus;
  action: 'RECEIVED' | 'CANCELLED' | 'REFUNDED';
  partialRefund: boolean;
  cancelledPaid: boolean;
  transferred: boolean;
  refundTotal?: number;
  cancelReason?: string;
  archived: boolean;
  /** `hc_orders.from_` — null on many real orders (older/imported ones): render nothing, never `sourceSystem.undefined`. */
  source?: SourceSystem;
  operator?: ActorRef;
  /**
   * The client of the row. Three kinds (1d, a reservation of an operator has the phone he typed and no client): found — an ordinary block; a phone
   * and nobody behind it — `phone` in the full format and NO `id`, NO `name`; no phone — no block at all.
   */
  client?: {
    id?: number;
    name?: string;
    phone?: string;
    vip: boolean;
    blacklist: boolean;
    legal: boolean;
    staff: boolean;
    firstOrder: boolean;
  };
  recipient?: { id?: number; name?: string; phone?: string };
  storeId: number;
  cityId: number;
  countryId: number;
  currency: Currency;
  deliveryType: DeliveryType;
  deliverySlot?: { from: string; to: string };
  items: Array<{ title: UiLabel; weight?: number; amount: number; type: ProductInfoType }>;
  itemCount: number;
  decor: boolean;
  inscription: boolean;
  hasNote: boolean;
  photoCount: number;
  fileCount: number;
  total: number;
  hasDiscount: boolean;
  paymentTypes: PaymentType[];
  paid: boolean;
  debt: boolean;
  feedback?: { avg?: number; count: number; waiting: boolean };
  commentCount: number;
  callCount: number;
  transferredTo?: OrderRef;
  transferredFrom?: { uniqueNumber: string; id?: number };
  note?: string;
  decorText?: string;
  fileNames?: string[];
  discounts?: Array<{ title: UiLabel; value: number }>;
}

export interface ArchiveOrderPage {
  list: ArchiveOrderRow[];
  hasNext: boolean;
  next?: { cursor: string };
  search?: ArchiveSearchContext;
}

// ---------- GET /summary ----------

export interface ArchiveSummaryTiles {
  all: number;
  received: number;
  cancelled: number;
  cancelledPaid: number;
  refunded: number;
  refundedFull: number;
  refundedPartial: number;
  money: CurrencyAmount[];
}

export interface ArchiveSummaryDay {
  day: string;
  count: number;
  money: CurrencyAmount[];
}

export interface ArchiveSummary {
  dateFrom: string;
  dateTo: string;
  status?: ArchiveStatus;
  withClient: boolean;
  tiles: ArchiveSummaryTiles;
  found: number;
  money: CurrencyAmount[];
  walkInHidden: number;
  days: ArchiveSummaryDay[];
}

// ---------- GET /clients/{clientId}/summary ----------

export interface ArchiveClientSummary {
  client: {
    id: number;
    name?: string;
    phone: string;
    countryId?: number;
    birthDate?: string;
    since?: string;
    legal: boolean;
    companyTitle?: string;
    vip?: string;
    blacklist?: string;
    note?: string;
  };
  city?: { id: number; title: UiLabel };
  callsLast30Days: number;
  frequentRecipient?: { id: number; name?: string; phone?: string; orders: number };
  stats: {
    orders: number;
    received: number;
    cancelled: number;
    refunded: number;
    money: CurrencyAmount[];
    avgCheck: CurrencyAmount[];
    lastOrder?: { id: number; uniqueNumber: string; date: string; feedbackAvg?: number; feedbackWaiting: boolean };
  };
  topProducts: Array<{ productInfoId: number; title: UiLabel; orders: number }>;
  months: Array<{ month: string; count: number; cancelled: number; money: CurrencyAmount[] }>;
  /** Q8: orders in scope where this client is only the *recipient* (not the buyer) — «Показать» pivots the list to `?q=<phone>&client_switch=false`. */
  asRecipient: number;
}

// ---------- GET /{orderId} ----------

export interface ArchiveOrderCardAlert {
  kind: 'CANCELLED' | 'CANCELLED_PAID' | 'REFUND_FULL' | 'REFUND_PARTIAL' | 'DEBT' | 'WITHOUT_RECEIPT' | 'TRANSFERRED';
  at?: string;
  actor?: ActorRef;
  /**
   * `CANCELLED` / `CANCELLED_PAID`: only the label of the reason («Клиент передумал», «Не дозвонились», …, «Другое») — or, for a
   * cancellation made before the comment was kept apart, the old text («подпись: комментарий»). Never the operator's comment of a new one.
   */
  reason?: string;
  /** `CANCELLED` / `CANCELLED_PAID`: the operator's comment — for staff only, the customer is never shown it. Absent without one. */
  comment?: string;
  amount?: number;
}

export interface ArchiveOrderCardDiscount {
  promoId: number;
  title: UiLabel;
  resultType: 'PERCENT_DISCOUNT' | 'MONEY_DISCOUNT' | 'FIXED_PRICE' | 'BONUS_PRODUCT';
  promoValue?: number;
  productAmount: number;
  value: number;
  enabled: boolean;
}

export interface ArchiveOrderItem {
  productId: number;
  productInfoId: number;
  priceId: number;
  title: UiLabel;
  type: ProductInfoType;
  status: string;
  removed: boolean;
  weight?: number;
  priceValue: number;
  amount: number;
  value: number;
  refundedAmount: number;
  discounts: ArchiveOrderCardDiscount[];
  /** True only for a line actually removed by an edit — NOT set (or false) for a line whose order was
   * simply cancelled wholesale (status flips to CANCELLED for every line in that case too). Missing on
   * older backends; callers fall back to `status === 'CANCELLED'` (§F44). */
  removedOnEdit?: boolean;
  /** 1d: `true` — a decoration service (the old editor's «Оформление»: photo print, inscription, decor). Absent (never `false`) for every other line. */
  decoration?: boolean;
  /** 1d: only for a line added AFTER the order was made — the store-local moment of the save that put it in, and who saved. Absent otherwise (and for every line when the history of the saves is not readable). */
  addedAt?: string;
  addedBy?: ActorRef;
}

export interface ArchiveOrderDiscount {
  promoId: number;
  title: UiLabel;
  resultType: string;
  promoValue?: number;
  enabled: boolean;
  appliedBy: 'AUTO' | 'PROMO_CODE' | 'OTP' | 'EMPLOYEE' | 'MANUAL';
  promoCode?: string;
  actor?: ActorRef;
  lines: Array<{ productId: number; productAmount: number; value: number; enabled: boolean }>;
  total: number;
}

export interface ArchiveOrderCard {
  id: number;
  uniqueNumber: string;
  status: ArchiveStatus;
  action: string;
  /** `archiveRow=false` → the order is still in progress; `inProgress` then carries the "open in Orders" link. */
  archiveRow: boolean;
  archived: boolean;
  partialRefund: boolean;
  cancelledPaid: boolean;
  inProgress?: { id: number; uniqueNumber: string; action: string };
  /** null on many real orders — see `ArchiveOrderRow.source`. */
  source?: SourceSystem;
  /**
   * Where the order came from as the operator said it when she made it («через WhatsApp», «через Instagram»; the order itself stays «колл-центр»). Absent — a call, or an order made before
   * the channel was kept. Not in the list row, not in the filter: the system column is `source`.
   */
  channel?: 'WHATSAPP' | 'INSTAGRAM';
  /** Only `code` — no server-side dictionary lookup; the frontend resolves it via `HxDictionaryService`
   * (`DictionaryKey.utmReferrer`, same source the old order card used) and falls back to the raw code. */
  referrer?: { code: string };
  createdAt?: string;
  createdBy?: ActorRef;
  date: string;
  receiveDate?: string;
  currency: Currency;
  total: number;
  subTotal: number;
  note?: string;
  decor: boolean;
  inscription: boolean;
  decorText?: string;
  clientType: ArchiveClientTypeFilter;
  withoutReceipt: boolean;
  store: {
    id: number;
    title: UiLabel;
    address?: string;
    cityId: number;
    cityTitle: UiLabel;
    countryId: number;
    countryTitle: UiLabel;
    countryDomain: string;
    timezone: string;
    brandId: number;
    printLocale: string;
    active: boolean;
    removed: boolean;
  };
  transferredTo?: OrderRef;
  transferredFrom?: { uniqueNumber: string; id?: number };
  alert?: ArchiveOrderCardAlert;
  /** As the row's: a reservation's typed phone with nobody behind it comes as `{ phone, … }` without `id` and `name`. */
  client?: {
    id?: number;
    name?: string;
    phone?: string;
    birthDate?: string;
    /** `c_client_brand_properties.vip_`/`blacklisted_` — booleans on the wire; some backends still send
     * the legacy non-empty string "VIP"/"1". Only ever truthy-checked, never rendered as a value. */
    vip?: boolean | string;
    blacklist?: boolean | string;
    clientNote?: string;
    legal: boolean;
    staff: boolean;
    ordersCount: number;
    since?: string;
  };
  recipient?: { id?: number; name?: string; phone?: string };
  company?: {
    title?: string;
    uin?: string;
    address?: string;
    contract?: string;
    authority?: { representative?: string; number?: string; date?: string; file?: ArchiveFile };
  };
  calls: Array<{
    callId: string;
    type: 'IN' | 'OUT';
    at: string;
    talkSeconds: number;
    answered: boolean;
    operator?: ActorRef;
    phone?: string;
    hasAudio: boolean;
  }>;
  receiving: {
    type: DeliveryType;
    /** The archive never sends it: the «Заказы» drawer lays the live state's planned receiving time over the card
     * (`GET /orders-current/{id}` → `receiving.plannedAt`, store-local) — see `ArchiveOrderReceivingComponent`. */
    plannedAt?: string;
    slot?: { date: string; from: string; to: string };
    timeRange?: 'ANY_TIME' | 'AFTER' | 'BEFORE' | 'ON_TIME';
    /** §F71: «19:00» — only for ON_TIME/BEFORE/AFTER; absent on older data (backend rollout in progress). */
    deliveryTime?: string;
    address?: string;
    admDiv?: string;
    entrance?: string;
    floor?: string;
    flat?: string;
    addressNote?: string;
    courier?: { id: number; name?: string; phone?: string };
  };
  items: ArchiveOrderItem[];
  discounts: ArchiveOrderDiscount[];
  totals: { items: number; discounts: number; total: number; refunded?: number; totalAfterRefund?: number };
  files: ArchiveFile[];
  /** Only ISSUED documents (§8.3) — reprint goes through `eventId`, never a `type`-based generator call. */
  documents: Array<{ type: 'payment' | 'product' | 'service'; at: string; actor?: ActorRef; eventId: number }>;
  payment: {
    paid: boolean;
    paidAt?: string;
    acceptedBy?: ActorRef;
    storePaymentType?: string;
    lines: PaymentLine[];
    debt: boolean;
    cashback?: number;
    channel?: PaymentChannel;
    fiscal: { kind: 'WEBKASSA' | 'ATOL' | 'NONE' | 'WITHOUT_RECEIPT'; fiscalNumber?: string; fiscalDate?: string };
  };
  refunds: Array<{
    order: OrderRef;
    at?: string;
    actor?: ActorRef;
    reason?: string;
    full: boolean;
    total: number;
    items: Array<{ title: UiLabel; amount: number; value: number }>;
    payments: PaymentLine[];
    fiscalNumber?: string;
  }>;
}

// ---------- GET /{orderId}/history ----------

export interface ArchiveEvent {
  id: number;
  type: string;
  at: string;
  actor?: ActorRef;
  info?: string;
  repeat?: number;
  editing?: { startedAt: string; finishedAt: string };
  sourceOrder?: OrderRef;
  link?: { kind: 'REFUND' | 'TRANSFER'; order: OrderRef };
  amount?: number;
  company?: { title?: string; uin?: string; contract?: string };
  expand?: 'DIFF' | 'PAYMENT' | 'SMS' | 'RECEIPT';
}

// ---------- GET /{orderId}/events/{eventId} ----------

export type ArchiveDiffFieldKind =
  | 'MONEY' | 'TEXT' | 'BOOLEAN' | 'ENUM' | 'DATETIME' | 'SLOT' | 'CLIENT' | 'STORE' | 'USER' | 'PHONE' | 'ADDRESS_PARTS'
  /** §F71: `deliveryTimeRange` as `{range, time?}` — the backend is migrating off the plain `ENUM` shape
   * for this one field; `archive-event-diff.component.ts` still reads the old plain string too. */
  | 'DELIVERY_TIME';

export interface ArchiveDiffField {
  key: string;
  kind: ArchiveDiffFieldKind;
  enumType?: string;
  before?: unknown;
  after?: unknown;
}

export interface ArchiveDiff {
  currency: Currency;
  noChanges: boolean;
  counts: { fields: number; items: number; discounts: number; files: number; payments: number };
  fields: ArchiveDiffField[];
  payments: Array<{ op: 'ADDED' | 'REMOVED' | 'CHANGED'; type: PaymentType; before?: PaymentLine; after?: PaymentLine }>;
  items: Array<{
    op: 'ADDED' | 'REMOVED' | 'CHANGED';
    productInfoId: number;
    title: UiLabel;
    type: ProductInfoType;
    priceValue?: number;
    amountBefore?: number;
    amountAfter?: number;
    valueBefore?: number;
    valueAfter?: number;
  }>;
  discounts: Array<{
    op: 'ADDED' | 'REMOVED' | 'MOVED' | 'RECALCULATED' | 'DISABLED' | 'ENABLED';
    promoId: number;
    title: UiLabel;
    resultType: string;
    promoValue?: number;
    before?: { productId: number; itemTitle?: UiLabel; productAmount: number; value: number; enabled: boolean };
    after?: { productId: number; itemTitle?: UiLabel; productAmount: number; value: number; enabled: boolean };
  }>;
  files: Array<{ op: 'ADDED' | 'REMOVED'; file: ArchiveFile }>;
  /** Snapshot used for `before` was written asynchronously and may postdate the event (§8.3) — UI shows a soft warning. */
  approximate?: boolean;
}

export type ArchiveEventDetails =
  | { kind: 'DIFF'; diff: ArchiveDiff }
  | {
      kind: 'PAYMENT';
      payment: { paidAt?: string; acceptedBy?: ActorRef; storePaymentType?: string; lines: PaymentLine[]; total: number; cashback?: number };
    }
  | {
      kind: 'SMS';
      sms: { phone?: string; text?: string; createdAt: string; sentAt?: string; status?: string; lastStatusAt?: string; segments?: number };
    };

// ---------- GET /{orderId}/feedback ----------

/** A comment of the card: the library's model and — 1d — the position of the employee who wrote it («оператор КЦ»; absent for a client, the system, an unknown author). */
export type ArchiveComment = CommentModel & { actorPosition?: string };

export interface ArchiveFeedbackTab {
  feedbacks: FeedbackModel[];
  comments: ArchiveComment[];
}

@Injectable({
  providedIn: 'root'
})
export class OrdersArchiveService {

  constructor(
    private http: HttpClient,
  ) {
  }

  getScope(): Observable<ArchiveScope> {
    return this.http.get<ArchiveScope>(`${ARCHIVE_BASE}/scope`);
  }

  getList(params: ArchiveListPageParams): Observable<ArchiveOrderPage> {
    return this.http.get<ArchiveOrderPage>(`${ARCHIVE_BASE}/list`, { params: this.toListParams(params) });
  }

  getSummary(params: ArchiveListParams & { withClient?: boolean }): Observable<ArchiveSummary> {
    return this.http.get<ArchiveSummary>(`${ARCHIVE_BASE}/summary`, { params: this.toListParams(params) });
  }

  getClientSummary(clientId: number): Observable<ArchiveClientSummary> {
    return this.http.get<ArchiveClientSummary>(`${ARCHIVE_BASE}/clients/${clientId}/summary`);
  }

  getOrderCard(orderId: number): Observable<ArchiveOrderCard> {
    return this.http.get<ArchiveOrderCard>(`${ARCHIVE_BASE}/${orderId}`);
  }

  getHistory(orderId: number): Observable<ArchiveEvent[]> {
    return this.http.get<ArchiveEvent[]>(`${ARCHIVE_BASE}/${orderId}/history`);
  }

  getEventDetails(orderId: number, eventId: number): Observable<ArchiveEventDetails> {
    return this.http.get<ArchiveEventDetails>(`${ARCHIVE_BASE}/${orderId}/events/${eventId}`);
  }

  getFeedbackTab(orderId: number): Observable<ArchiveFeedbackTab> {
    return this.http.get<ArchiveFeedbackTab>(`${ARCHIVE_BASE}/${orderId}/feedback`);
  }

  /** `receiptOrderId` may arrive as `null`: in templates `$event?.id` yields `null`, not `undefined`,
   * for the order's own receipt — `receipt_order_id=null` used to go out and fail with 404. */
  getReceipt(orderId: number, receiptOrderId?: number | null): Observable<ReceiptModel> {
    let params = new HttpParams();
    if (receiptOrderId != null) {
      params = params.set('receipt_order_id', String(receiptOrderId));
    }
    return this.http.get<ReceiptModel>(`${ARCHIVE_BASE}/${orderId}/receipt`, { params });
  }

  /**
   * Reprint of an already-issued legal-entity document (§8.3: read-only, keyed by `eventId`, never
   * a `type`-based generator call — that would write a new `*_INVOICE_GENERATED` event from a
   * read-only archive page). Precedent: `HxOrderService.generateInvoice` — same arraybuffer +
   * Content-Disposition contract, caller `saveAs()`s the blob.
   */
  /**
   * Recording of one of this order's calls — the archive's own endpoint, scoped exactly like the card
   * (cc + the caller's stores); in the call-center every user may listen, `call_record_download` is for
   * the other apps. Deliberately NOT the shared `/callcenter/calls/{id}/url` (other pages keep their rules).
   */
  getCallAudioUrl(orderId: number, callId: string): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${ARCHIVE_BASE}/${orderId}/calls/${encodeURIComponent(callId)}/audio-url`);
  }

  getDocument(orderId: number, eventId: number): Observable<HttpResponse<ArrayBuffer>> {
    return this.http.get(`${ARCHIVE_BASE}/${orderId}/documents/${eventId}`, { responseType: 'arraybuffer', observe: 'response' });
  }

  private toListParams(filter: ArchiveListPageParams): HttpParams {
    let params = new HttpParams();
    const set = (name: string, value?: string | number | boolean): void => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params = params.set(name, String(value));
      }
    };
    const setArr = (name: string, values?: Array<string | number>): void => {
      (values ?? []).forEach(v => {
        params = params.append(name, String(v));
      });
    };
    set('date_from', filter.dateFrom);
    set('date_to', filter.dateTo);
    setArr('country_ids', filter.countryIds);
    setArr('city_ids', filter.cityIds);
    setArr('store_ids', filter.storeIds);
    set('delivery_type', filter.deliveryType);
    setArr('sources', filter.sources);
    set('operator_id', filter.operatorId);
    set('with_client', filter.withClient);
    set('decor', filter.decor);
    set('attachments', filter.attachments);
    set('client_type', filter.clientType);
    setArr('payment_types', filter.paymentTypes);
    set('feedback', filter.feedback);
    set('comments', filter.comments);
    setArr('product_info_ids', filter.productInfoIds);
    set('courier_id', filter.courierId);
    set('fiscal_number', filter.fiscalNumber);
    set('refund_from', filter.refundFrom);
    set('refund_to', filter.refundTo);
    set('status', filter.status);
    set('sort', filter.sort);
    set('limit', filter.limit);
    set('cursor', filter.cursor);
    set('q', filter.q);
    set('client_id', filter.clientId);
    set('client_switch', filter.clientSwitch);
    return params;
  }
}
